<script setup lang="ts">
/**
 * One field of the auth forms: underlined input, floating label, optional
 * reveal toggle for passwords, inline error (Figma `66:46882`, `66:47293`).
 *
 * Two things it owns that the callers must not have to remember:
 *  - autofill sync, so a password manager filling the form can never leave the
 *    submit button stuck disabled (`66:47545`)
 *  - `type="password"` — `FairInputText` hard-codes `type="text"`, so the real
 *    input rides in its slot
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairInputText } from '@/ui/primitives/FairInputText'
// `Visibility` (`I66:46893;1359:63597`) is a 16px glyph pair of its own; the
// shared `show`/`hide` icons are drawn on a different grid (23.6×18).
import eyeIconUrl from '@/features/auth/ui/icons/eye.svg'
import eyeOffIconUrl from '@/features/auth/ui/icons/eye-off.svg'
import { useAutofillSync } from '@/features/auth/model/useAutofillSync'

const props = withDefaults(defineProps<{
  modelValue: string
  label: string
  inputId: string
  type?: 'text' | 'email' | 'password'
  autocomplete?: string
  invalid?: boolean
  errorText?: string
}>(), {
  type: 'text',
  autocomplete: undefined,
  invalid: false,
  errorText: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  focus: []
  blur: []
}>()

const { t } = useI18n()

const inputEl = ref<HTMLInputElement | null>(null)
const isFocused = ref(false)
const revealed = ref(false)

// Bridges the composable's write-back into the parent's v-model.
const model = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value),
})

useAutofillSync(inputEl, model)

const isPassword = computed(() => props.type === 'password')

const inputType = computed(() => {
  if (!isPassword.value) return props.type
  return revealed.value ? 'text' : 'password'
})

/** The label sits on the baseline until there is something to sit above. */
const floating = computed(() => isFocused.value || props.modelValue.length > 0)

function handleInput(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement).value)
}
</script>

<template>
  <div class="fair-auth-field">
    <FairInputText
      variant="underline"
      :invalid="invalid"
      :focused="isFocused"
    >
      <label
        :for="inputId"
        :class="['fair-auth-field__label', floating ? 'fair-auth-field__label--floating' : '']"
      >
        {{ label }}
      </label>

      <input
        :id="inputId"
        ref="inputEl"
        class="fair-input-text__control type-body-ui fair-auth-field__control"
        :type="inputType"
        :value="modelValue"
        :autocomplete="autocomplete"
        :aria-invalid="invalid || undefined"
        :aria-describedby="errorText ? `${inputId}-error` : undefined"
        @input="handleInput"
        @focus="isFocused = true; emit('focus')"
        @blur="isFocused = false; emit('blur')"
      >

      <button
        v-if="isPassword"
        type="button"
        class="fair-auth-form__reveal"
        :aria-label="revealed ? t('signup.hide_password') : t('signup.show_password')"
        @click="revealed = !revealed"
      >
        <img
          class="fair-auth-form__reveal-icon"
          :src="revealed ? eyeOffIconUrl : eyeIconUrl"
          alt=""
        >
      </button>
    </FairInputText>

    <p
      v-if="errorText"
      :id="`${inputId}-error`"
      class="fair-auth-form__error"
    >
      {{ errorText }}
    </p>
  </div>
</template>
